import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, SxProps, Theme } from '@mui/material';
import React from 'react';
import { Palette } from 'Palette';
import { StyledTypography } from './styles';

export interface StyledCalloutProps {
  variant: 'warning' | 'error' | 'info';
  children: React.ReactNode;
  size?: 'small' | 'default';
  className?: string;
  sx?: SxProps<Theme>;
  icon?: React.ReactNode;
}

/**
 * StyledCallout component for displaying warning and error messages with icons.
 * Uses Material UI components and Inter font from styled components.
 */
export const StyledCallout: React.FC<StyledCalloutProps> = ({
  variant,
  children,
  size = 'default',
  className,
  sx,
  icon,
}) => {
  // Use 'danger' for error variant (Palette uses 'danger' for error states)
  // Note: StyledTypography references Palette.error, but Palette only has 'danger'
  // Use 'active' for info variant (blue colors)
  const palette =
    variant === 'warning'
      ? Palette.warning
      : variant === 'error'
        ? Palette.error || Palette.danger
        : Palette.active; // info variant uses blue/active palette
  const isSmall = size === 'small';
  const defaultIcon =
    variant === 'warning' ? (
      <WarningIcon sx={{ fontSize: isSmall ? '1.25rem' : '1.5rem' }} />
    ) : variant === 'error' ? (
      <ErrorIcon sx={{ fontSize: isSmall ? '1.25rem' : '1.5rem' }} />
    ) : (
      <InfoIcon sx={{ fontSize: isSmall ? '1.25rem' : '1.5rem' }} />
    );

  return (
    <Box
      className={`react-StyledCallout ${className || ''}`.trim()}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: isSmall ? 1.5 : 2,
        padding: isSmall ? '0.75rem' : '1rem',
        backgroundColor: palette.background,
        border: `1px solid ${palette.border}`,
        borderRadius: isSmall ? '6px' : '8px',
        fontFamily: 'Inter, sans-serif',
        ...sx,
      }}
    >
      <Box
        sx={{
          color: palette.text,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {icon || defaultIcon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <StyledTypography
          variant={isSmall ? 'caption' : 'body2'}
          tone={variant === 'warning' ? 'warning' : variant === 'error' ? 'default' : 'default'}
          sx={{
            color: palette.text,
            fontFamily: 'Inter, sans-serif',
            fontSize: isSmall ? '0.875rem' : undefined,
          }}
        >
          {children}
        </StyledTypography>
      </Box>
    </Box>
  );
};

export default StyledCallout;
